import React, { useState, useEffect, useMemo } from 'react';
import { restaurantsAPI, cyclesAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogActionBar } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Plus, Search, Upload, Building2, Phone, Mail, MapPin, Pencil, Trash2, Download, FileSignature, Megaphone, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { RestaurantForm, emptyRestaurantForm, formToPayload } from '../components/RestaurantForm';
import RestaurantDrawer from '../components/RestaurantDrawer';
import CycleChips from '../components/CycleChips';
import { usePagination, PaginationBar } from '../components/PaginationBar';
import DuplicateRestaurantsBanner from '../components/DuplicateRestaurantsBanner';
import { byName } from '../lib/sort';
import { useAuth } from '../context/AuthContext';

const Restaurants = () => {
  const { isReadOnly } = useAuth();
  const [dupKey, setDupKey] = useState(0);
  const [restaurants, setRestaurants] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [cycleFilter, setCycleFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [drawer, setDrawer] = useState({ restaurant: null, tab: 'details', cycleId: null });
  const [formData, setFormData] = useState(emptyRestaurantForm());

  useEffect(() => {
    fetchRestaurants();
    cyclesAPI.list().then((r) => setCycles((r.data || []).filter((c) => c.status === 'active'))).catch(() => {});
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await restaurantsAPI.getAll();
      setRestaurants(response.data);
      setDrawer((d) => (d.restaurant ? { ...d, restaurant: response.data.find((r) => r.id === d.restaurant.id) || d.restaurant } : d));
    } catch (error) {
      toast.error('Failed to fetch restaurants');
    } finally {
      setLoading(false);
    }
  };

  const openDrawer = (restaurant, tab = 'details', cycleId = null) => setDrawer({ restaurant, tab, cycleId });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = formToPayload(formData);
    try {
      let res;
      try {
        res = await restaurantsAPI.create(payload);
      } catch (err) {
        const detail = err.response?.status === 409 ? err.response?.data?.detail : null;
        if (!detail) throw err;
        if (!window.confirm(`${detail.message}\n\nCreate it anyway as a separate restaurant?`)) {
          const existing = restaurants.find((r) => r.id === detail.existing_id);
          setIsModalOpen(false);
          if (existing) openDrawer(existing, 'details');
          return;
        }
        res = await restaurantsAPI.create({ ...payload, allow_duplicate: true });
      }
      toast.success('Restaurant created — send the Industry-Partner Agreement from the Contracts tab.');
      setIsModalOpen(false);
      setFormData(emptyRestaurantForm());
      await fetchRestaurants();
      setDupKey((k) => k + 1);
      openDrawer(res.data, 'contracts');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error((detail && typeof detail === 'object' ? detail.message : detail) || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this restaurant?')) return;
    try {
      await restaurantsAPI.delete(id);
      toast.success('Restaurant deleted successfully');
      fetchRestaurants();
    } catch (error) {
      // Backend returns { detail: { message, placements, contracts, feedback_forms } }
      // when the delete is blocked because references still exist.
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        toast.error(detail.message, { duration: 8000 });
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to delete restaurant');
      }
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const response = await restaurantsAPI.upload(file);
      toast.success(`Imported ${response.data.imported} restaurants`);
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} rows skipped: ${response.data.errors.slice(0, 3).join(' · ')}${response.data.errors.length > 3 ? ' …' : ''}`, { duration: 9000 });
      }
      setIsUploadModalOpen(false);
      fetchRestaurants();
      setDupKey((k) => k + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const filteredRestaurants = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const list = restaurants.filter((r) =>
      (r.name.toLowerCase().includes(q) || r.cuisine_type.toLowerCase().includes(q) || r.address.toLowerCase().includes(q))
      && (cycleFilter === 'all' || (r.cycles || []).some((c) => c.cycle_id === cycleFilter)),
    );
    if (cycleFilter === 'all') return list.sort(byName);
    const open = (r) => { const c = (r.cycles || []).find((x) => x.cycle_id === cycleFilter); return c ? c.capacity - c.filled : 0; };
    return list.sort((a, b) => open(b) - open(a) || byName(a, b));
  }, [restaurants, searchQuery, cycleFilter]);
  const filterCycleId = cycleFilter === 'all' ? null : cycleFilter;
  const pg = usePagination(filteredRestaurants, 'restaurants');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="restaurants-page" className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-ica-navy">Restaurants & Partners</h1>
            <p className="text-gray-500 mt-1">Manage your placement partner restaurants</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button
              data-testid="download-restaurant-template-btn"
              variant="outline"
              onClick={async () => {
                try {
                  const res = await restaurantsAPI.downloadTemplate();
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a'); a.href = url;
                  a.download = 'restaurant_upload_template.xlsx'; a.click();
                  window.URL.revokeObjectURL(url);
                } catch { toast.error('Failed to download template'); }
              }}
              className="border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Template
            </Button>
            <Button
              data-testid="upload-restaurants-btn"
              variant="outline"
              onClick={() => setIsUploadModalOpen(true)}
             
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Excel
            </Button>
            <Button
              data-testid="add-restaurant-btn"
              onClick={() => { setFormData(emptyRestaurantForm()); setIsModalOpen(true); }}
              
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Restaurant
            </Button>
          </div>
        </div>

        <DuplicateRestaurantsBanner key={dupKey} canWrite={!isReadOnly} onMerged={fetchRestaurants} />

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              data-testid="search-restaurants"
              placeholder="Search restaurants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={cycleFilter} onValueChange={setCycleFilter}>
            <SelectTrigger data-testid="restaurants-cycle-filter" className="w-72">
              <Layers className="w-4 h-4 mr-2 text-ica-navy shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All active cycles</SelectItem>
              {cycles.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`restaurants-cycle-filter-opt-${c.id}`}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filterCycleId && (
            <span className="text-xs text-gray-500" data-testid="cycle-filter-hint">Sorted by open seats · {filteredRestaurants.length} in this cycle</span>
          )}
        </div>

        {filteredRestaurants.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building2 className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-gray-500 text-center">
                {searchQuery ? 'No restaurants found matching your search.' : 'No restaurants yet. Add your first restaurant to get started.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="border rounded-lg bg-white">
            <PaginationBar {...pg.bar} position="top" noun="restaurants" className="rounded-t-lg" />
            {/* Phone: stacked cards */}
            <div className="sm:hidden divide-y divide-gray-100" data-testid="restaurants-card-list">
              {pg.pageItems.map((restaurant) => (
                <div
                  key={restaurant.id}
                  data-testid={`restaurant-card-${restaurant.id}`}
                  className="px-4 py-3 space-y-2 active:bg-gray-50"
                  onClick={(e) => { if (e.target.closest('button')) return; openDrawer(restaurant, 'details'); }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-ica-gold/10 rounded-lg flex items-center justify-center shrink-0">
                      <Building2 className="w-4 h-4 text-ica-gold" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-ica-navy truncate">{restaurant.name}</div>
                      <div className="text-xs text-gray-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" />{restaurant.address}</div>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-ica-gold/10 text-ica-gold-dark shrink-0">{restaurant.cuisine_type}</span>
                  </div>
                  <div className="text-xs text-gray-600 space-y-0.5 pl-12">
                    <p className="text-gray-800">{restaurant.contact_person}</p>
                    {restaurant.phone && <p className="flex items-center gap-1 truncate"><Phone className="w-3 h-3 shrink-0" />{restaurant.phone}</p>}
                    {restaurant.email && <p className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0" />{restaurant.email}</p>}
                  </div>
                  <div className="pl-12">
                    <CycleChips
                      cycles={restaurant.cycles}
                      total={restaurant.filled_positions}
                      filterCycleId={filterCycleId}
                      testIdPrefix={`cycle-chip-card-${restaurant.id}`}
                      onChipClick={(c) => openDrawer(restaurant, 'placements', c.cycle_id)}
                    />
                  </div>
                  <div className="flex justify-end gap-1 -mr-2">
                    <button data-testid={`contracts-restaurant-card-${restaurant.id}`} onClick={() => openDrawer(restaurant, 'contracts')} className="p-2 hover:bg-ica-gold/10 text-ica-gold-dark rounded-lg" title="Contracts"><FileSignature className="w-4 h-4" /></button>
                    <button data-testid={`industry-letter-restaurant-card-${restaurant.id}`} onClick={() => openDrawer(restaurant, 'industry')} className="p-2 hover:bg-ica-navy/10 text-ica-navy rounded-lg" title="Industry letter"><Megaphone className="w-4 h-4" /></button>
                    <button data-testid={`edit-restaurant-card-${restaurant.id}`} onClick={() => openDrawer(restaurant, 'details')} className="p-2 hover:bg-gray-100 rounded-lg" title="Edit"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-restaurant-card-${restaurant.id}`} onClick={() => handleDelete(restaurant.id)} className="p-2 hover:bg-red-50 text-red-500 rounded-lg" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="table-scroll hidden sm:block">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Restaurant</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Contact</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Location</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cuisine</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[340px]">Placements by cycle</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pg.pageItems.map((restaurant) => (
                  <tr key={restaurant.id} data-testid={`restaurant-row-${restaurant.id}`} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={(e) => {
                    if (e.target.closest('button')) return;
                    openDrawer(restaurant, 'details');
                  }}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-ica-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-ica-gold" />
                        </div>
                        <span className="font-medium text-ica-navy">{restaurant.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 hidden md:table-cell">
                      <div className="space-y-0.5 text-sm">
                        <p className="text-gray-800">{restaurant.contact_person}</p>
                        <div className="flex items-center gap-3 text-gray-500">
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{restaurant.phone}</span>
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{restaurant.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 hidden lg:table-cell">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="truncate max-w-[160px]">{restaurant.address}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-ica-gold/10 text-ica-gold-dark">
                        {restaurant.cuisine_type}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <CycleChips
                        cycles={restaurant.cycles}
                        total={restaurant.filled_positions}
                        filterCycleId={filterCycleId}
                        testIdPrefix={`cycle-chip-${restaurant.id}`}
                        onChipClick={(c) => openDrawer(restaurant, 'placements', c.cycle_id)}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-1">
                        <button
                          data-testid={`contracts-restaurant-${restaurant.id}`}
                          onClick={(e) => { e.stopPropagation(); openDrawer(restaurant, 'contracts'); }}
                          className="p-2 hover:bg-ica-gold/10 text-ica-gold-dark rounded-lg transition-colors"
                          title="Manage contracts (Industry-Partner Agreement)"
                        >
                          <FileSignature className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`industry-letter-restaurant-${restaurant.id}`}
                          onClick={(e) => { e.stopPropagation(); openDrawer(restaurant, 'industry'); }}
                          className="p-2 hover:bg-ica-navy/10 text-ica-navy rounded-lg transition-colors"
                          title="Industry Placement Confirmation Letter"
                        >
                          <Megaphone className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`edit-restaurant-${restaurant.id}`}
                          onClick={() => openDrawer(restaurant, 'details')}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit details"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-restaurant-${restaurant.id}`}
                          onClick={() => handleDelete(restaurant.id)}
                          className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <PaginationBar {...pg.bar} position="bottom" noun="restaurants" className="rounded-b-lg" />
          </div>
        )}

        {/* Create modal — editing happens in the drawer */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="restaurant-modal">
            <DialogActionBar>
              <Button type="button" size="sm" variant="outline" onClick={() => setIsModalOpen(false)} data-testid="cancel-restaurant-top">Cancel</Button>
              <Button type="button" size="sm" data-testid="save-restaurant-top" onClick={handleSubmit}>Create</Button>
            </DialogActionBar>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-ica-navy">Add New Restaurant</DialogTitle>
              <DialogDescription className="text-gray-500">Fill in the details to add a new partner restaurant.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <RestaurantForm formData={formData} setFormData={setFormData} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-restaurant-btn">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <RestaurantDrawer
          restaurant={drawer.restaurant}
          open={!!drawer.restaurant}
          onOpenChange={(o) => { if (!o) setDrawer({ restaurant: null, tab: 'details', cycleId: null }); }}
          initialTab={drawer.tab}
          focusCycleId={drawer.cycleId}
          onUpdated={fetchRestaurants}
        />

        {/* Upload Modal */}
        <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-ica-navy">Upload Restaurants</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Upload an Excel file (.xlsx) with restaurant data. The file should have columns:</p>
              <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                <li>name, address, contact_person, phone, email</li>
                <li>cuisine_type, capacity, available_positions, notes (optional)</li>
              </ul>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-ica-gold transition-colors">
                <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <label className="cursor-pointer">
                  <span className="text-ica-navy font-medium hover:text-ica-gold">Click to upload</span>
                  <span className="text-gray-500"> or drag and drop</span>
                  <input type="file" data-testid="upload-restaurants-input" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
                </label>
                <p className="text-xs text-gray-400 mt-2">Excel files only (.xlsx, .xls)</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Restaurants;
